import { useEffect } from "react";
import { DataFile } from "interfaces";

import { MultiSelect } from "ui-components/Select";

import { useFetchDataFile } from "modules/data-files/api";
import { useGetTypesForFile } from "modules/data-files/api";

interface Props {
  fileId: string;
  onChange: (columns: string[]) => unknown;
  currentSelection?: string[];

  allowedTypes?: DataFile.ColType[];
  exclusions?: string[];

  help?: React.ReactNode;
  name?: string;

  fullListing?: boolean;
  placeholder?: string;
}

const MultiColumnSelect = ({
  fileId,
  onChange,
  currentSelection = [],
  allowedTypes = [],
  exclusions = [],
  name,
  help,
  placeholder,
  fullListing = false,
}: Props) => {
  const { data } = useFetchDataFile(fileId, true);
  const columnTypes = useGetTypesForFile(fileId || "");

  useEffect(() => {
    if (
      (allowedTypes || []).length >= 1 &&
      (currentSelection || []).length >= 1 &&
      Object.keys(columnTypes || {}).length >= 1
    ) {
      const n = currentSelection.filter((s) =>
        allowedTypes.includes(columnTypes[s])
      );
      if (n.length < currentSelection.length) {
        onChange(n);
      }
    }
  }, [allowedTypes, currentSelection, columnTypes, onChange]);

  if (!data) {
    return null;
  }

  const columns = data.columns || [];
  const options = columns
    .filter((c) => {
      if (exclusions.includes(c)) {
        return false;
      }
      if (allowedTypes?.length >= 1) {
        return allowedTypes.includes(columnTypes[c]);
      }
      return true;
    })
    .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));

  if (fullListing) {
    return (
      <div className="md:grid md:grid-cols-3 md:gap-4">
        {options.map((c) => (
          <div
            key={c}
            role="option"
            aria-selected={currentSelection.includes(c)}
            aria-label={c}
          >
            <p
              className="cursor-pointer"
              onClick={() =>
                currentSelection.includes(c)
                  ? onChange(currentSelection.filter((s) => s !== c))
                  : onChange([...currentSelection, c])
              }
            >
              <span className="pr-1 mr-1">
                <input
                  key={`input-${c}`}
                  type="checkbox"
                  className="form-checkbox m-0 p-0 focus:ring-0"
                  checked={currentSelection.includes(c)}
                />
              </span>
              {c}
            </p>
          </div>
        ))}
      </div>
    );
  }

  return (
    <MultiSelect
      name={name}
      allowedValues={options}
      selected={currentSelection}
      onChange={onChange}
      help={help}
      placeholder={placeholder}
    />
  );
};

export default MultiColumnSelect;
